import { useRef, useState } from "react";
import { isNil } from "lodash";
import { useNavigate } from "react-router-dom";
import { Select, Button, Input as AntInput, notification } from "antd";
import { BiChevronLeft } from "react-icons/bi";
import { MdOutlineUploadFile, MdOutlineImage } from "react-icons/md";
import { useTranslation } from "react-i18next";
import Card from "shared/components/Card";
import Header from "shared/components/Header";
import Spin from "shared/components/Spin";
import { useQuery, useMutation } from "shared/hooks/useApi";
import useScreenJob from "shared/hooks/useScreenJob";
import { NAME_SPACES } from "shared/locales/constants";
import graphql from "utils/api/graphql";
import { SCREENS } from "utils/constants";
import { prepareMockupImage } from "utils/helpers/mockup-image";
import { createScreenDraft } from "utils/helpers/screenDraft";

const Create = () => {
  const navigate = useNavigate();
  const { t } = useTranslation(NAME_SPACES.PRIVATE.ADMIN.SCREEN_STUDIO);
  const CREATE = t("CREATE", { returnObjects: true });
  const [processId, setProcessId] = useState(null);

  const { data: processData, loading: loadingProcesses } = useQuery(
    graphql.queries.PROCESSES,
    { variables: { take: 100, orderBy: [{ name: "ASC" }] } },
  );

  const processes = processData?.processes?.data || [];
  const selectedProcess = processes.find((p) => p.id === processId);
  const statuses = selectedProcess?.processStatuses || [];
  const sortedStatuses = [...statuses].sort((a, b) => a.order - b.order);

  if (loadingProcesses) return <Spin />;

  return (
    <>
      <Header
        title={CREATE.HEADER.TITLE}
        actions={[
          {
            text: CREATE.HEADER.BACK,
            onClick: () =>
              navigate(SCREENS.PRIVATE.ADMIN.SCREEN_STUDIOS.LIST.path),
            type: "gray--link",
            icon: <BiChevronLeft size={20} />,
          },
        ]}
      />
      <Card type="simple">
        <div className="m-bottom-16">
          <label className="input__label">{CREATE.PROCESS}</label>
          <Select
            className="screen-studio-create__select"
            placeholder={CREATE.PROCESS_PLACEHOLDER}
            value={processId}
            onChange={setProcessId}
            showSearch
            filterOption={(input, option) =>
              (option?.label || "")
                .toLowerCase()
                .indexOf(input.toLowerCase()) >= 0
            }
            options={processes.map((p) => ({ value: p.id, label: p.name }))}
          />
        </div>

        {processId && (
          <ManualTab statuses={sortedStatuses} navigate={navigate} />
        )}
      </Card>
    </>
  );
};

/**
 * Creates a screen from pasted HTML and CSS.
 * CSS goes into styleContent, never into the HTML: the renderer injects it
 * for the whole version, so all its states share it.
 */
const ManualTab = ({ statuses, navigate }) => {
  const { t } = useTranslation(NAME_SPACES.PRIVATE.ADMIN.SCREEN_STUDIO);
  const CREATE = t("CREATE", { returnObjects: true });
  const [statusId, setStatusId] = useState(null);
  const [name, setName] = useState("");
  const [html, setHtml] = useState("");
  const [css, setCss] = useState("");
  const [instructions, setInstructions] = useState("");
  const [generatedStates, setGeneratedStates] = useState([]);
  const [creating, setCreating] = useState(false);
  const [generating, setGenerating] = useState(false);
  const fileInputRef = useRef(null);

  const [addScreen] = useMutation(graphql.mutations.ADD_SCREEN);
  const [addScreenVersion] = useMutation(graphql.mutations.ADD_SCREEN_VERSION);
  const [detectSlots] = useMutation(graphql.mutations.DETECT_SLOTS);
  const { generateHtml, generateState } = useScreenJob();

  /**
   * Fills the HTML and CSS fields from a mockup instead of creating the screen
   * straight away, so the result can be reviewed before anything is persisted.
   */
  const handleUpload = async (event) => {
    const file = event.target.files && event.target.files[0];
    event.target.value = "";
    if (!file || generating) return;
    setGenerating(true);
    try {
      const image = await prepareMockupImage(file);
      const note = instructions.trim() || undefined;
      const generated = await generateHtml({ image, instructions: note });
      if (!generated) return;
      setHtml(generated.html || "");
      setCss(generated.css || "");

      const drawn = [];
      let abandoned = false;
      const variants = generated.states || [];
      for (let index = 0; index < variants.length; index++) {
        const variant = variants[index];
        try {
          const state = await generateState({
            image,
            styleContent: generated.css,
            htmlContent: generated.html,
            conditionField: variant.conditionField || undefined,
            conditionValue: variant.conditionValue || undefined,
            instructions: note,
          });
          if (!state) {
            abandoned = true;
            break;
          }
          drawn.push({
            ...state,
            name: variant.name || state.name,
            order: isNil(variant.order) ? index + 1 : variant.order,
            conditions: variant.conditions || state.conditions,
          });
        } catch (_error) {}
      }
      if (!abandoned && variants.length && !drawn.length) {
        notification.error({ message: CREATE.GENERATE_ERROR });
      }
      setGeneratedStates(drawn);
    } catch (error) {
      notification.error({
        message: error.reason || CREATE.GENERATE_ERROR || error.message,
      });
    } finally {
      setGenerating(false);
    }
  };

  const handleCreate = async () => {
    if (!statusId || !html.trim() || creating) return;
    setCreating(true);
    try {
      const { screenId } = await createScreenDraft({
        addScreen,
        addScreenVersion,
        detectSlots,
        statusId,
        description: name.trim() || CREATE.DEFAULT_NAME,
        htmlContent: html,
        styleContent: css,
        slots: [],
        states: generatedStates,
        copyStates: generatedStates.length > 0,
      });
      navigate(
        `${SCREENS.PRIVATE.ADMIN.SCREEN_STUDIOS.DETAIL.path}/${screenId}`,
      );
    } catch (error) {
      notification.error({
        message: CREATE.CREATE_ERROR || error.message,
      });
    } finally {
      setCreating(false);
    }
  };

  return (
    <div className="screen-studio-create__manual">
      <div className="m-bottom-16">
        <label className="input__label">{CREATE.STATUS}</label>
        <Select
          className="screen-studio-create__select"
          showSearch
          placeholder={CREATE.STATUS_PLACEHOLDER}
          value={statusId}
          onChange={setStatusId}
          filterOption={(input, option) =>
            (option?.label || "").toLowerCase().indexOf(input.toLowerCase()) >=
            0
          }
          options={statuses.map((status) => ({
            value: status.id,
            label: `${status.order} · ${status.name}`,
          }))}
        />
      </div>

      <div className="m-bottom-16">
        <label className="input__label">{CREATE.NAME}</label>
        <AntInput
          className="screen-studio-create__field"
          placeholder={CREATE.NAME_PLACEHOLDER}
          value={name}
          onChange={(e) => setName(e.target.value)}
        />
      </div>

      <div className="screen-studio-create__generate m-bottom-16">
        <label className="input__label">{CREATE.GENERATE}</label>
        <AntInput
          className="screen-studio-create__field"
          placeholder={CREATE.INSTRUCTIONS_PLACEHOLDER}
          value={instructions}
          onChange={(e) => setInstructions(e.target.value)}
        />
        <input
          ref={fileInputRef}
          type="file"
          accept="image/*"
          className="screen-studio-create__file"
          onChange={handleUpload}
        />
        <Button
          className="m-top-8"
          loading={generating}
          disabled={creating}
          onClick={() => fileInputRef.current?.click()}
          icon={<MdOutlineImage />}
        >
          {CREATE.UPLOAD}
        </Button>
        <p className="screen-studio-create__hint">{CREATE.GENERATE_HINT}</p>
      </div>

      <div className="m-bottom-16">
        <label className="input__label">{CREATE.HTML}</label>
        <AntInput.TextArea
          rows={14}
          placeholder={CREATE.HTML_PLACEHOLDER}
          value={html}
          onChange={(e) => setHtml(e.target.value)}
        />
      </div>

      <div className="m-bottom-16">
        <label className="input__label">{CREATE.CSS}</label>
        <AntInput.TextArea
          rows={8}
          placeholder={CREATE.CSS_PLACEHOLDER}
          value={css}
          onChange={(e) => setCss(e.target.value)}
        />
      </div>

      <Button
        type="primary"
        onClick={handleCreate}
        loading={creating}
        disabled={!statusId || !html.trim()}
        icon={<MdOutlineUploadFile />}
      >
        {CREATE.SUBMIT}
      </Button>
      <p className="screen-studio-create__hint">{CREATE.HINT}</p>
    </div>
  );
};

export default Create;
